import { useState, type FormEvent } from 'react';
import { Turnstile, useConfig } from '../components/Turnstile';
import { PasswordInput } from '../components/fields';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useMe } from '../lib/hooks';
import { Button, Loading } from '../components/ui';
import { AuthLayout } from './Auth';

interface Preview { state: 'open' | 'accepted' | 'expired' | 'revoked'; email: string; hasAccount: boolean }

function InviteFlow({ preview, title, sub, onAccept, signupHref, loginHref }: {
  preview: Preview; title: string; sub: React.ReactNode; onAccept: () => Promise<void>; signupHref: string; loginHref: string;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (preview.state !== 'open') {
    return (
      <AuthLayout title="Convite indisponível" sub="Este convite expirou, foi revogado ou já foi usado. Peça um novo a quem convidou você.">
        <Link to="/" className="btn dark lg block">Ir para o Mesa</Link>
      </AuthLayout>
    );
  }
  const logged = !!me.data;
  const sameEmail = logged && me.data!.user.email.toLowerCase() === preview.email.toLowerCase();
  return (
    <AuthLayout title={title} sub={sub}>
      <div className="small" style={{ marginBottom: 14 }}>Convite para <strong className="mono" style={{ color: 'var(--text)' }}>{preview.email}</strong></div>
      {me.isLoading ? (
        <Loading rows={1} height={46} />
      ) : sameEmail ? (
        <Button
          variant="dark" size="lg" block loading={busy}
          onClick={async () => {
            setBusy(true);
            setError('');
            try {
              await onAccept();
              await qc.invalidateQueries({ queryKey: ['me'] });
            } catch (e) {
              setError(errMsg(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Aceitar convite
        </Button>
      ) : logged ? (
        <>
          <div className="error-box" style={{ marginTop: 0, marginBottom: 12 }}>
            Você entrou como <strong>{me.data!.user.email}</strong>. Entre com {preview.email} para aceitar.
          </div>
          <Button
            block size="lg"
            onClick={async () => {
              await api.post('/api/auth/logout').catch(() => undefined);
              await qc.resetQueries();
              location.assign(preview.hasAccount ? loginHref : signupHref);
            }}
          >
            Trocar de conta
          </Button>
        </>
      ) : preview.hasAccount ? (
        <Link to={loginHref} className="btn dark lg block">Entrar para aceitar</Link>
      ) : (
        <Link to={signupHref} className="btn dark lg block">Criar conta e aceitar</Link>
      )}
      {error && <div className="error-box" role="alert">{error}</div>}
    </AuthLayout>
  );
}

export function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const q = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<Preview & { tenantNome: string; inviterNome: string; roleLabel: string }>(`/api/invites/${token}`),
    retry: false,
  });
  if (q.isLoading) return <AuthLayout title="Convite" sub="Carregando…"><Loading rows={2} height={44} /></AuthLayout>;
  if (!q.data) return <AuthLayout title="Convite não encontrado" sub={errMsg(q.error)}><Link to="/" className="btn dark lg block">Ir para o Mesa</Link></AuthLayout>;
  const p = q.data;
  const back = `/convite/${token}`;
  return (
    <InviteFlow
      preview={p}
      title={`Convite para ${p.tenantNome}`}
      sub={<><strong style={{ color: 'var(--text)' }}>{p.inviterNome}</strong> convidou você para participar como <strong style={{ color: 'var(--text)' }}>{p.roleLabel}</strong>.</>}
      loginHref={`/entrar?email=${encodeURIComponent(p.email)}&next=${encodeURIComponent(back)}`}
      signupHref={`/cadastro?convite=${token}&email=${encodeURIComponent(p.email)}`}
      onAccept={async () => {
        const r = await api.post<{ tenantId: string }>(`/api/invites/${token}/accept`);
        nav(`/t/${r.tenantId}`, { replace: true });
      }}
    />
  );
}

/**
 * Quem recebe o link do relatório (cliente/restaurante) cai direto num cadastro de 2 campos:
 * nome e uma senha fácil (6+ caracteres, pode ser só números). Se já tem conta, só a senha.
 */
function QuickAccess({ token, p }: { token: string; p: Preview & { inviterNome: string; resourceLabel: string } }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [reset, setReset] = useState(0);
  const needsCaptcha = !!useConfig().data?.turnstileSiteKey;
  const signup = !p.hasAccount;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (signup && nome.trim().length < 3) return setError('Escreva seu nome.');
    if (senha.length < 6) return setError('A senha precisa ter pelo menos 6 caracteres.');
    if (needsCaptcha && !captcha) return setError('Aguarde a verificação anti-robô.');
    setBusy(true);
    try {
      let next = '/portal';
      if (signup) {
        const r = await api.post<{ next?: string }>('/api/auth/signup', { nome, email: p.email, senha, conta: 'empresa', shareToken: token, turnstileToken: captcha });
        next = r.next ?? next;
      } else {
        await api.post('/api/auth/login', { email: p.email, senha, turnstileToken: captcha });
        next = (await api.post<{ next: string }>(`/api/share-invites/${token}/accept`)).next;
      }
      await qc.resetQueries();
      nav(next, { replace: true });
    } catch (err) {
      setError(errMsg(err));
      setReset((x) => x + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={signup ? 'Crie seu acesso' : 'Entre para ver'}
      sub={<><strong style={{ color: 'var(--text)' }}>{p.inviterNome}</strong> compartilhou com você: <strong style={{ color: 'var(--text)' }}>{p.resourceLabel}</strong>.</>}
      below={!signup ? <Link to={`/esqueci`} className="link-btn u" style={{ fontSize: 12.5 }}>Esqueci minha senha</Link> : undefined}
    >
      <form onSubmit={submit} noValidate>
        <div className="field">
          <span className="label">Seu e-mail</span>
          <div className="mono" style={{ fontSize: 14, padding: '2px 0 4px' }}>{p.email}</div>
        </div>
        {signup && (
          <label className="field" style={{ cursor: 'default' }}>
            <span className="label">Seu nome</span>
            <input className="input" autoComplete="name" placeholder="Como você quer ser chamado" value={nome} onChange={(e) => setNome(e.target.value)} />
          </label>
        )}
        <label className="field" style={{ cursor: 'default' }}>
          <span className="label">{signup ? 'Crie uma senha' : 'Sua senha'}</span>
          <PasswordInput autoComplete={signup ? 'new-password' : 'current-password'} inputMode={signup ? 'text' : undefined} placeholder={signup ? 'Pelo menos 6 letras ou números' : ''} value={senha} onChange={(e) => setSenha(e.target.value)} />
          {signup && <span className="small" style={{ lineHeight: 1.45 }}>Pode ser só números. Escolha algo fácil de lembrar; você vai usar este e-mail e esta senha para entrar.</span>}
        </label>
        <Turnstile onToken={setCaptcha} resetKey={reset} />
        {error && <div className="error-box" role="alert">{error}</div>}
        <Button variant="dark" size="lg" block type="submit" loading={busy} style={{ marginTop: 18 }}>{signup ? 'Criar acesso e ver relatório' : 'Entrar e ver relatório'}</Button>
      </form>
    </AuthLayout>
  );
}

export function AcceptShare() {
  const { token } = useParams();
  const nav = useNavigate();
  const me = useMe();
  const q = useQuery({
    queryKey: ['share-invite', token],
    queryFn: () => api.get<Preview & { inviterNome: string; resourceLabel: string; permLabel: string }>(`/api/share-invites/${token}`),
    retry: false,
  });
  if (q.isLoading || me.isLoading) return <AuthLayout title="Compartilhamento" sub="Carregando…"><Loading rows={2} height={44} /></AuthLayout>;
  if (!q.data) return <AuthLayout title="Compartilhamento não encontrado" sub={errMsg(q.error)}><Link to="/" className="btn dark lg block">Ir para o Mesa</Link></AuthLayout>;
  const p = q.data;
  if (!me.data && p.state === 'open') return <QuickAccess token={token!} p={p} />;
  const back = `/compartilhado/${token}`;
  return (
    <InviteFlow
      preview={p}
      title={p.resourceLabel}
      sub={<><strong style={{ color: 'var(--text)' }}>{p.inviterNome}</strong> compartilhou com você ({p.permLabel}).</>}
      loginHref={`/entrar?email=${encodeURIComponent(p.email)}&next=${encodeURIComponent(back)}`}
      signupHref={`/cadastro?compartilhado=${token}&conta=empresa&email=${encodeURIComponent(p.email)}`}
      onAccept={async () => {
        const r = await api.post<{ next: string }>(`/api/share-invites/${token}/accept`);
        nav(r.next, { replace: true });
      }}
    />
  );
}
